import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  MultiSelect,
  Select,
  checkboxOptionRenderer,
  defaultMultiSelectDisplayRenderer
} from '@siftscience/focus-components/select'
import { MenuItemRenderer } from '@siftscience/focus-components/dropdown'
import { TextInput } from '@siftscience/focus-components/input'
import { Search } from '@siftscience/focus-components/icons/Search'
import { Download } from '@siftscience/focus-components/icons/'
import { Button } from '@siftscience/focus-components/button'
import { Title } from '@siftscience/focus-components/text'
import {
  NONE_MERCHANT,
  useMerchantContext
} from '../../template-tooling/context/merchant-context'
import TemplateToolingHeaderDropdown from '../../template-tooling/dropdown'

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    gap: '8px'
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: '4px',
    '& .focus-select-button': {
      width: '220px'
    }
  }
}))

export interface ISelectProps {
  label: string
  value: string
}

export interface ActionProps {
  setSelectedMID: (selectedMID: string[]) => void
  sortBy: ISelectProps
  setSortBy: (selectedMID: ISelectProps) => void
  search: string
  setSearch: (search: string) => void
  midSelectItems: ISelectProps[]
  sortSelectItems: ISelectProps[]
  setShowUploadModal: (isShow: boolean) => void
  totalImages: number
}

const Actions = ({
  search,
  setSearch,
  setSortBy,
  sortBy,
  setSelectedMID,
  midSelectItems,
  setShowUploadModal,
  sortSelectItems
}: ActionProps) => {
  const classes = useStyles()
  const { displayMerchantList, selectedMerchant, setSelectedMerchant } =
    useMerchantContext()

  const onSortChange = (value: ISelectProps) => {
    setSortBy(value)
  }
  const handleSearch = event => {
    setSearch(event.target.value)
  }

  const isActionDisabled =
    !selectedMerchant || selectedMerchant.id === NONE_MERCHANT.id

  return (
    <div className={classes.container}>
      <div className={classes.wrapper}>
        <TemplateToolingHeaderDropdown
          title="Merchant"
          items={displayMerchantList}
          onSelect={setSelectedMerchant}
          selectedItem={selectedMerchant}
          placeholder="Select Merchant"
        />
      </div>
      <div className={classes.wrapper}>
        <Title size="xsmall" color="secondary">
          MID
        </Title>
        <MultiSelect<ISelectProps>
          variant="secondary"
          placeholder="All MIDs"
          disabled={isActionDisabled}
          items={midSelectItems}
          selectedItemsRenderer={items => {
            if (items.length === 0) {
              return <span>All MIDs selected</span>
            }

            if (items.length === 1) {
              return <span>{items[0].label}</span>
            }

            return <span>{items.length} MIDs selected</span>
          }}
          displayRenderer={({ placeholder, ...params }) =>
            defaultMultiSelectDisplayRenderer({
              ...params,
              placeholder: placeholder as string
            })
          }
          menuItemRenderer={params => {
            const { onMouseMove, ...rendererProps } = params.props ?? {}

            return (checkboxOptionRenderer as MenuItemRenderer)({
              ...params,
              props: rendererProps
            })
          }}
          onChange={selectedOptions => {
            setSelectedMID(selectedOptions.map(({ value }) => value))
          }}
        />
      </div>
      <div className={classes.wrapper}>
        <Title size="xsmall" color="secondary">
          Sort by
        </Title>
        <Select
          items={sortSelectItems}
          getItemLabel={(item: ISelectProps) => item.label}
          selectedItem={sortBy}
          onChange={onSortChange}
          disabled={isActionDisabled}
          placeholder="Sort by"
          size="medium"
          stretch={true}
          variant="secondary"
        />
      </div>
      <div className={classes.wrapper}>
        <Title size="xsmall" color="secondary">
          Search
        </Title>
        <TextInput
          icon={<Search />}
          size={'medium'}
          disabled={isActionDisabled}
          placeholder={'Search by title'}
          value={search}
          onChange={handleSearch}
        />
      </div>

      <Button
        className={classes.button}
        size="large"
        variant="primary"
        disabled={isActionDisabled}
        onClick={() => setShowUploadModal(true)}
      >
        <Download />
        <Title size="xsmall">Upload Images</Title>
      </Button>
    </div>
  )
}

export default Actions
